//#region src/cp-audit/vocabulary.ts
/**
* Every object type an audit row may name in `resource_type`, with its console label.
*
* `org` and `organization` both exist because `tenant_base_channel.set` wrote `org` and the
* organization routes wrote `organization`. Old rows keep their string, so both stay.
*/
const CP_AUDIT_RESOURCE_TYPES = {
	access_request: { label: "Access request" },
	alert_destination: { label: "Alert destination" },
	alert_rule: { label: "Alert rule" },
	alert_silence: { label: "Alert silence" },
	app: { label: "App" },
	app_composition: { label: "App version" },
	app_compute_override: { label: "App compute override" },
	app_provisioning: { label: "App provisioning" },
	audit_log: { label: "Audit log" },
	auth_provider: { label: "Sign-in provider" },
	break_glass_grant: { label: "Break-glass grant" },
	channel: { label: "Channel" },
	channel_share: { label: "Channel share" },
	crate_name: { label: "Crate name" },
	data_plane: { label: "Data plane" },
	dcr_lifecycle_settings: { label: "Client registration settings" },
	dcr_redirect_host: { label: "Client redirect host" },
	deployment_config: { label: "Deployment setting" },
	dp_access_request: { label: "Data plane access request" },
	dp_config_override: { label: "Data plane config override" },
	dp_fixture: { label: "Data plane fixture" },
	fixture_config: { label: "Fixture config" },
	group: { label: "Group" },
	invitation: { label: "Invitation" },
	machine_principal: { label: "Machine principal" },
	mail: { label: "Mail" },
	metering: { label: "Metering" },
	mimir_rules: { label: "Metrics rules" },
	oauth_auth_request: { label: "OAuth consent request" },
	oauth_client: { label: "OAuth client" },
	oauth_grant: { label: "OAuth grant" },
	oauth_link: { label: "OAuth account link" },
	observability_settings: { label: "Observability settings" },
	operator: { label: "Operator" },
	operator_session: { label: "Operator session" },
	org: { label: "Tenant" },
	org_asset: { label: "Organization asset" },
	org_signing_key: { label: "Organization signing key" },
	organization: { label: "Organization" },
	passkey: { label: "Passkey" },
	platform_channel: { label: "Platform channel" },
	pod: { label: "Pod" },
	pricing_rate: { label: "Pricing rate" },
	recovery_code: { label: "Recovery code" },
	release_stage: { label: "Release stage" },
	resource_grant: { label: "Resource grant" },
	role: { label: "Role" },
	scim_client: { label: "SCIM client" },
	step_up_request: { label: "Step-up request" },
	table: { label: "Table" },
	user: { label: "User" },
	version: { label: "Platform version" }
};
/**
* Every action a CP service may write, keyed by the action string.
*
* The groups follow the service that writes each action today. A group comment names the plan
* decision for each action this plan adds. Every other action is a string the tree wrote before.
*/
const CP_AUDIT_ACTIONS = {
	login_success: {
		resourceType: null,
		label: "Signed in with a password"
	},
	login_failed: {
		resourceType: null,
		label: "Password sign-in failed"
	},
	passkey_login: {
		resourceType: null,
		label: "Signed in with a passkey"
	},
	passkey_login_failed: {
		resourceType: null,
		label: "Passkey sign-in failed"
	},
	oauth_login: {
		resourceType: null,
		label: "Signed in with an identity provider"
	},
	oauth_login_failed: {
		resourceType: null,
		label: "Identity provider sign-in failed"
	},
	token_refresh: {
		resourceType: null,
		label: "Session refreshed"
	},
	token_refresh_refused: {
		resourceType: null,
		label: "Session refresh refused"
	},
	password_changed: {
		resourceType: null,
		label: "Password changed"
	},
	logout: {
		resourceType: "operator_session",
		label: "Signed out"
	},
	logout_noop: {
		resourceType: "operator_session",
		label: "Sign-out found no session"
	},
	session_revoked_all: {
		resourceType: "operator",
		label: "All sessions revoked"
	},
	mcp_token_minted: {
		resourceType: "operator_session",
		label: "MCP token issued"
	},
	mcp_token_revoked: {
		resourceType: "operator_session",
		label: "MCP token revoked"
	},
	identity_token_minted: {
		resourceType: "operator_session",
		label: "Identity token issued"
	},
	passkey_registered: {
		resourceType: "passkey",
		label: "Passkey added"
	},
	passkey_removed: {
		resourceType: "passkey",
		label: "Passkey removed"
	},
	passkeys_admin_reset: {
		resourceType: "operator",
		label: "Passkeys reset by an administrator"
	},
	second_factor_passed: {
		resourceType: "passkey",
		label: "Second factor passed"
	},
	second_factor_failed: {
		resourceType: "passkey",
		label: "Second factor failed"
	},
	recovery_codes_issued: {
		resourceType: "recovery_code",
		label: "Recovery codes issued"
	},
	recovery_code_used: {
		resourceType: "recovery_code",
		label: "Recovery code used"
	},
	recovery_failed: {
		resourceType: "operator",
		label: "Account recovery failed"
	},
	recovery_email_sent: {
		resourceType: "operator",
		label: "Recovery email sent"
	},
	recovery_email_verified: {
		resourceType: "operator",
		label: "Recovery email verified"
	},
	recovery_email_unavailable: {
		resourceType: "operator",
		label: "Recovery email not sent"
	},
	recovery_notice_sent: {
		resourceType: "operator",
		label: "Recovery notice sent"
	},
	step_up_requested: {
		resourceType: "step_up_request",
		label: "Step-up requested"
	},
	step_up_approved: {
		resourceType: "step_up_request",
		label: "Step-up approved"
	},
	step_up_denied: {
		resourceType: "step_up_request",
		label: "Step-up denied"
	},
	step_up_redeemed: {
		resourceType: "step_up_request",
		label: "Step-up redeemed"
	},
	step_up_redeem_refused: {
		resourceType: "step_up_request",
		label: "Step-up redemption refused"
	},
	step_up_failed: {
		resourceType: "step_up_request",
		label: "Step-up failed"
	},
	break_glass_armed: {
		resourceType: "break_glass_grant",
		label: "Break-glass access armed"
	},
	break_glass_used: {
		resourceType: "break_glass_grant",
		label: "Break-glass access used"
	},
	break_glass_refused: {
		resourceType: "break_glass_grant",
		label: "Break-glass access refused"
	},
	session_revoked: {
		resourceType: "operator_session",
		label: "Session revoked"
	},
	operator_role_activated: {
		resourceType: "operator_session",
		label: "Active role switched"
	},
	operator_created: {
		resourceType: "operator",
		label: "Operator created"
	},
	operator_updated: {
		resourceType: "operator",
		label: "Operator updated"
	},
	operator_disabled: {
		resourceType: "operator",
		label: "Operator disabled"
	},
	operator_activated: {
		resourceType: "operator",
		label: "Operator activated"
	},
	operator_role_changed: {
		resourceType: "operator",
		label: "Operator role changed"
	},
	invitation_created: {
		resourceType: "invitation",
		label: "Invitation created"
	},
	invitation_mail_queued: {
		resourceType: "invitation",
		label: "Invitation mail queued"
	},
	invitation_mail_unavailable: {
		resourceType: "invitation",
		label: "Invitation mail not sent"
	},
	invitation_revoked: {
		resourceType: "invitation",
		label: "Invitation revoked"
	},
	invitation_accepted: {
		resourceType: "operator",
		label: "Invitation accepted"
	},
	invitation_accept_url_revealed: {
		resourceType: "invitation",
		label: "Invitation link revealed"
	},
	access_requested: {
		resourceType: "access_request",
		label: "Access requested"
	},
	access_approved: {
		resourceType: "access_request",
		label: "Access request approved"
	},
	access_denied: {
		resourceType: "access_request",
		label: "Access request denied"
	},
	access_request_reopened: {
		resourceType: "access_request",
		label: "Access request reopened"
	},
	access_superseded: {
		resourceType: "access_request",
		label: "Access request superseded"
	},
	role_create: {
		resourceType: "role",
		label: "Role created"
	},
	role_update: {
		resourceType: "role",
		label: "Role updated"
	},
	role_delete: {
		resourceType: "role",
		label: "Role deleted"
	},
	member_role_grant: {
		resourceType: "operator",
		label: "Role granted to an operator"
	},
	member_role_revoke: {
		resourceType: "operator",
		label: "Role revoked from an operator"
	},
	group_role_grant: {
		resourceType: "group",
		label: "Role granted to a group"
	},
	group_role_revoke: {
		resourceType: "group",
		label: "Role revoked from a group"
	},
	resource_grant: {
		resourceType: "resource_grant",
		label: "Resource access granted"
	},
	resource_revoke: {
		resourceType: "resource_grant",
		label: "Resource access revoked"
	},
	group_create: {
		resourceType: "group",
		label: "Group created"
	},
	group_update: {
		resourceType: "group",
		label: "Group updated"
	},
	group_delete: {
		resourceType: "group",
		label: "Group deleted"
	},
	group_member_add: {
		resourceType: "group",
		label: "Group member added"
	},
	group_member_remove: {
		resourceType: "group",
		label: "Group member removed"
	},
	scim_user_create: {
		resourceType: "operator",
		label: "SCIM user created"
	},
	scim_user_update: {
		resourceType: "operator",
		label: "SCIM user updated"
	},
	scim_user_delete: {
		resourceType: "operator",
		label: "SCIM user deleted"
	},
	scim_group_create: {
		resourceType: "group",
		label: "SCIM group created"
	},
	scim_group_update: {
		resourceType: "group",
		label: "SCIM group updated"
	},
	scim_group_delete: {
		resourceType: "group",
		label: "SCIM group deleted"
	},
	scim_client_create: {
		resourceType: "scim_client",
		label: "SCIM client created"
	},
	scim_client_update: {
		resourceType: "scim_client",
		label: "SCIM client updated"
	},
	scim_client_rotate: {
		resourceType: "scim_client",
		label: "SCIM client token rotated"
	},
	scim_client_delete: {
		resourceType: "scim_client",
		label: "SCIM client deleted"
	},
	auth_provider_created: {
		resourceType: "auth_provider",
		label: "Sign-in provider created"
	},
	auth_provider_updated: {
		resourceType: "auth_provider",
		label: "Sign-in provider updated"
	},
	auth_provider_deleted: {
		resourceType: "auth_provider",
		label: "Sign-in provider deleted"
	},
	auth_provider_bootstrapped: {
		resourceType: "auth_provider",
		label: "Sign-in provider set at startup"
	},
	oauth_client_created: {
		resourceType: "oauth_client",
		label: "OAuth client created"
	},
	oauth_client_updated: {
		resourceType: "oauth_client",
		label: "OAuth client updated"
	},
	oauth_client_update_failed: {
		resourceType: "oauth_client",
		label: "OAuth client update failed"
	},
	oauth_client_deleted: {
		resourceType: "oauth_client",
		label: "OAuth client deleted"
	},
	oauth_client_delete_failed: {
		resourceType: "oauth_client",
		label: "OAuth client delete failed"
	},
	oauth_client_self_registered: {
		resourceType: "oauth_client",
		label: "OAuth client registered itself"
	},
	oauth_clients_reaped: {
		resourceType: "oauth_client",
		label: "Unused OAuth clients deleted"
	},
	oauth_consent_approved: {
		resourceType: "oauth_auth_request",
		label: "OAuth consent approved"
	},
	oauth_consent_denied: {
		resourceType: "oauth_auth_request",
		label: "OAuth consent denied"
	},
	oauth_grant_created: {
		resourceType: "oauth_grant",
		label: "OAuth grant created"
	},
	oauth_grant_revoked: {
		resourceType: "oauth_grant",
		label: "OAuth grant revoked"
	},
	oauth_grant_reuse_detected: {
		resourceType: "oauth_grant",
		label: "OAuth grant reuse detected"
	},
	oauth_link_created: {
		resourceType: "oauth_link",
		label: "Identity provider account linked"
	},
	oauth_link_deleted: {
		resourceType: "oauth_link",
		label: "Identity provider account unlinked"
	},
	"dcr_lifecycle_settings.update": {
		resourceType: "dcr_lifecycle_settings",
		label: "Client registration settings updated"
	},
	dcr_redirect_host_created: {
		resourceType: "dcr_redirect_host",
		label: "Client redirect host added"
	},
	dcr_redirect_host_updated: {
		resourceType: "dcr_redirect_host",
		label: "Client redirect host updated"
	},
	dcr_redirect_host_update_failed: {
		resourceType: "dcr_redirect_host",
		label: "Client redirect host update failed"
	},
	dcr_redirect_host_deleted: {
		resourceType: "dcr_redirect_host",
		label: "Client redirect host deleted"
	},
	dcr_redirect_host_delete_failed: {
		resourceType: "dcr_redirect_host",
		label: "Client redirect host delete failed"
	},
	dcr_redirect_host_rejected: {
		resourceType: "dcr_redirect_host",
		label: "Client redirect host refused"
	},
	machine_token_exchange: {
		resourceType: "machine_principal",
		label: "Machine token exchanged"
	},
	"machine_principal.key_registered": {
		resourceType: "machine_principal",
		label: "Machine key registered"
	},
	"machine_principal.key_revoked": {
		resourceType: "machine_principal",
		label: "Machine key revoked"
	},
	"machine_principal.created": {
		resourceType: "machine_principal",
		label: "Machine principal created"
	},
	"machine_principal.disabled": {
		resourceType: "machine_principal",
		label: "Machine principal disabled"
	},
	"machine_principal.trust_policy_created": {
		resourceType: "machine_principal",
		label: "Machine trust policy created"
	},
	"machine_principal.trust_policy_disabled": {
		resourceType: "machine_principal",
		label: "Machine trust policy disabled"
	},
	"machine_principal.trust_policy_token_ttl_set": {
		resourceType: "machine_principal",
		label: "Machine trust policy token lifetime set"
	},
	"door_pull_principal.ensured": {
		resourceType: "machine_principal",
		label: "Door-pull principal converged"
	},
	"door_pull_principal.key_registered": {
		resourceType: "machine_principal",
		label: "Door-pull principal key registered"
	},
	"door_pull_principal.disabled": {
		resourceType: "machine_principal",
		label: "Door-pull principal disabled"
	},
	"door_pull_principal.token_minted": {
		resourceType: "machine_principal",
		label: "Door-pull bootstrap token minted"
	},
	"data_plane.door_pull_switch_set": {
		resourceType: "data_plane",
		label: "Door-pull switch set"
	},
	"data_plane.door_pull_key_enrolled": {
		resourceType: "data_plane",
		label: "Door-pull key enrolled by a data plane"
	},
	"data_plane.door_pull_kid_changed": {
		resourceType: "data_plane",
		label: "Door-pull key id reported by a data plane changed"
	},
	mail_sent: {
		resourceType: "mail",
		label: "Mail sent"
	},
	mail_failed: {
		resourceType: "mail",
		label: "Mail failed"
	},
	mail_refused_limit: {
		resourceType: "mail",
		label: "Mail refused by the send limit"
	},
	mail_refused_disabled: {
		resourceType: "mail",
		label: "Mail refused because mail is off"
	},
	"audit_log.exported": {
		resourceType: "audit_log",
		label: "Audit log exported"
	},
	"org.create": {
		resourceType: "organization",
		label: "Organization created"
	},
	"org.update": {
		resourceType: "organization",
		label: "Organization updated"
	},
	"org.internal_update": {
		resourceType: "organization",
		label: "Organization updated by a service"
	},
	"org.suspend": {
		resourceType: "organization",
		label: "Organization suspended"
	},
	"org.unsuspend": {
		resourceType: "organization",
		label: "Organization unsuspended"
	},
	"org.decommission": {
		resourceType: "organization",
		label: "Organization decommissioned"
	},
	"org.restore": {
		resourceType: "organization",
		label: "Organization restored"
	},
	"org.assign_dp": {
		resourceType: "organization",
		label: "Organization assigned to a data plane"
	},
	"org.set_data_location": {
		resourceType: "organization",
		label: "Organization data location set"
	},
	"org.migrate": {
		resourceType: "organization",
		label: "Organization migrated to a data plane"
	},
	"org.assets_deleted": {
		resourceType: "organization",
		label: "Organization assets deleted"
	},
	"org.assets_bucket_created": {
		resourceType: "organization",
		label: "Organization asset bucket created"
	},
	branding_updated: {
		resourceType: "organization",
		label: "Branding updated"
	},
	shell_updated: {
		resourceType: "organization",
		label: "Shell settings updated"
	},
	mail_limit_updated: {
		resourceType: "organization",
		label: "Mail limit updated"
	},
	"pricing_rate.update": {
		resourceType: "pricing_rate",
		label: "Pricing rates updated"
	},
	"alerting.rule_upserted": {
		resourceType: "alert_rule",
		label: "Alert rule saved"
	},
	"alerting.rule_override_deleted": {
		resourceType: "alert_rule",
		label: "Alert rule override deleted"
	},
	"alerting.destinations_updated": {
		resourceType: "alert_destination",
		label: "Alert destinations updated"
	},
	"alerting.dp_destinations_updated": {
		resourceType: "alert_destination",
		label: "Data plane alert destinations updated"
	},
	"alerting.destination_tested": {
		resourceType: "alert_destination",
		label: "Test alert sent"
	},
	"alerting.silence_created": {
		resourceType: "alert_silence",
		label: "Alert silence created"
	},
	"alerting.silence_expired": {
		resourceType: "alert_silence",
		label: "Alert silence ended"
	},
	"tenant_minimum_ring.set": {
		resourceType: "organization",
		label: "Tenant minimum ring set"
	},
	"tenant_minimum_ring.clear": {
		resourceType: "organization",
		label: "Tenant minimum ring cleared"
	},
	"observability_settings.update": {
		resourceType: "observability_settings",
		label: "Observability settings updated"
	},
	"org_asset.upload_url_minted": {
		resourceType: "org_asset",
		label: "Asset upload link issued"
	},
	"org_asset.deleted": {
		resourceType: "org_asset",
		label: "Asset deleted"
	},
	"mimir.fixture_rules_pushed": {
		resourceType: "mimir_rules",
		label: "Fixture metrics rules pushed"
	},
	"metering.reconcile_triggered": {
		resourceType: "metering",
		label: "Metering reconcile started"
	},
	"org_signing_key.rotated": {
		resourceType: "org_signing_key",
		label: "Organization signing key rotated"
	},
	"org_signing_key.created": {
		resourceType: "org_signing_key",
		label: "Organization signing key created"
	},
	"retention.pruned": {
		resourceType: "table",
		label: "Old rows deleted"
	},
	"settings.retention_update": {
		resourceType: "observability_settings",
		label: "Retention settings updated"
	},
	"dp.dlq_replay": {
		resourceType: "data_plane",
		label: "Dead letter replayed"
	},
	"dp.dlq_discard": {
		resourceType: "data_plane",
		label: "Dead letter discarded"
	},
	"dp.k8s_query": {
		resourceType: "data_plane",
		label: "Data plane cluster queried"
	},
	"data_plane.trace_sampling_set": {
		resourceType: "data_plane",
		label: "Data plane trace sampling rate set"
	},
	"cp_pod_log.read": {
		resourceType: "pod",
		label: "Control plane pod log read"
	},
	"dp_logs.query": {
		resourceType: "data_plane",
		label: "Data plane logs queried"
	},
	"app_pod_log.read": {
		resourceType: "pod",
		label: "App pod log read"
	},
	"app_logs.query": {
		resourceType: "app",
		label: "App logs queried"
	},
	"app_fleet_logs.query": {
		resourceType: "app",
		label: "App logs queried across data planes"
	},
	"app_logs.tail": {
		resourceType: "app",
		label: "App log tail opened"
	},
	"mcp_tool.call": {
		resourceType: null,
		label: "MCP tool called"
	},
	"cli.request": {
		resourceType: null,
		label: "CLI request sent"
	},
	"app.create": {
		resourceType: "app",
		label: "App created"
	},
	"app.update": {
		resourceType: "app",
		label: "App updated"
	},
	"app.delete": {
		resourceType: "app",
		label: "App deleted"
	},
	"app.owner.add": {
		resourceType: "app",
		label: "App owner added"
	},
	"app.owner.remove": {
		resourceType: "app",
		label: "App owner removed"
	},
	"app.licence.grant": {
		resourceType: "app",
		label: "App licence granted"
	},
	"app.licence.revoke": {
		resourceType: "app",
		label: "App licence revoked"
	},
	"app.permission.create": {
		resourceType: "app",
		label: "App permission created"
	},
	"app.permission.delete": {
		resourceType: "app",
		label: "App permission deleted"
	},
	"app.permission.declaration.dropped": {
		resourceType: "app",
		label: "App permission declaration dropped"
	},
	"app.kernel-rights.request": {
		resourceType: "app",
		label: "Kernel rights requested"
	},
	"app.kernel-rights.approve": {
		resourceType: "app",
		label: "Kernel rights approved"
	},
	"app.kernel-rights.deny": {
		resourceType: "app",
		label: "Kernel rights denied"
	},
	"app.kernel-rights.revoke": {
		resourceType: "app",
		label: "Kernel rights revoked"
	},
	"app.kernel-rights.step-up-requested": {
		resourceType: "app",
		label: "Rights step-up requested"
	},
	"app.kernel-rights.step-up-refused": {
		resourceType: "app",
		label: "Rights step-up refused"
	},
	"app.library-rights.request": {
		resourceType: "app",
		label: "Library rights requested"
	},
	"app.library-rights.approve": {
		resourceType: "app",
		label: "Library rights approved"
	},
	"app.library-rights.deny": {
		resourceType: "app",
		label: "Library rights denied"
	},
	"app.library-rights.revoke": {
		resourceType: "app",
		label: "Library rights revoked"
	},
	"app.code-signing-key.create": {
		resourceType: "app",
		label: "Code signing key created"
	},
	"app.release-sources.replace": {
		resourceType: "app",
		label: "Release sources replaced"
	},
	"app.crate-names.replace": {
		resourceType: "app",
		label: "Crate names replaced"
	},
	"app.version_set_changed": {
		resourceType: "dp_fixture",
		label: "Fixture version set changed"
	},
	"app.compute_override.set": {
		resourceType: "app_compute_override",
		label: "App compute override set"
	},
	"app.compute_override.clear": {
		resourceType: "app_compute_override",
		label: "App compute override cleared"
	},
	"app.factory_ca_published": {
		resourceType: "app",
		label: "App device CA published"
	},
	"library.consumer_config.set": {
		resourceType: "app",
		label: "Library consumer config set"
	},
	"library.consumer_config.clear": {
		resourceType: "app",
		label: "Library consumer config cleared"
	},
	"library_instance.reaped": {
		resourceType: "app",
		label: "Orphan library instances deleted"
	},
	"app_version.run_published": {
		resourceType: "app",
		label: "App version published"
	},
	"app_version.run_publish_refused": {
		resourceType: "app",
		label: "App version publish refused"
	},
	"app_composition.bundle_register": {
		resourceType: "app_composition",
		label: "App bundle registered"
	},
	"app_composition.bundle_register.idempotent": {
		resourceType: "app_composition",
		label: "App bundle registered again"
	},
	"app_composition.deprecate": {
		resourceType: "app_composition",
		label: "App version deprecated"
	},
	"app_composition.fixture_manifest.resync": {
		resourceType: "app_composition",
		label: "Fixture manifest synced again"
	},
	"rights_certificate.issued": {
		resourceType: "app_composition",
		label: "Rights certificate issued"
	},
	"rights_certificate.renewed": {
		resourceType: "app_composition",
		label: "Rights certificate renewed"
	},
	"rights_certificate.narrowed": {
		resourceType: "app_composition",
		label: "Rights certificate narrowed"
	},
	"rights_certificate.rekeyed": {
		resourceType: "app_composition",
		label: "Rights certificate rekeyed"
	},
	"rights_certificate.withdrawn": {
		resourceType: "app_composition",
		label: "Rights certificate withdrawn"
	},
	"release_statement.signed": {
		resourceType: "app",
		label: "Release statement signed"
	},
	"release_statement.step_up_requested": {
		resourceType: "app",
		label: "Release signature step-up requested"
	},
	"release_statement.sign_refused": {
		resourceType: "app",
		label: "Release signature refused"
	},
	"app_channel_pin.set": {
		resourceType: "app",
		label: "App ring pin set"
	},
	"app_channel_pin.auto_bump_edge": {
		resourceType: "app",
		label: "Edge pin moved to a new version"
	},
	"app_channel_pin.promote": {
		resourceType: "app",
		label: "App promoted to a ring"
	},
	"app_channel_pin.demote": {
		resourceType: "app",
		label: "App demoted from a ring"
	},
	"app_channel_pin.unpin": {
		resourceType: "app",
		label: "App ring pin removed"
	},
	"fixture_config.config_set": {
		resourceType: "fixture_config",
		label: "Fixture config set"
	},
	"fixture_config.config_cleared": {
		resourceType: "fixture_config",
		label: "Fixture config cleared"
	},
	"channel.update": {
		resourceType: "channel",
		label: "Channel updated"
	},
	"channel.private.create": {
		resourceType: "channel",
		label: "Private channel created"
	},
	"channel.private.update": {
		resourceType: "channel",
		label: "Private channel updated"
	},
	"channel.private.delete": {
		resourceType: "channel",
		label: "Private channel deleted"
	},
	"channel.share.create": {
		resourceType: "channel_share",
		label: "Channel shared"
	},
	"channel.share.revoke": {
		resourceType: "channel_share",
		label: "Channel share revoked"
	},
	"channel.share.decided": {
		resourceType: "channel_share",
		label: "Channel share decided by a data plane"
	},
	"app.adoption.decided": {
		resourceType: "app",
		label: "App adoption decided by a data plane"
	},
	"tenant_base_channel.set": {
		resourceType: "org",
		label: "Tenant base channel set"
	},
	"user_channel_override.set": {
		resourceType: "user",
		label: "User channel override set"
	},
	"user_channel_override.clear": {
		resourceType: "user",
		label: "User channel override cleared"
	},
	"version.register": {
		resourceType: "version",
		label: "Platform version registered"
	},
	"version.promote": {
		resourceType: "version",
		label: "Platform version promoted"
	},
	"version.deprecate": {
		resourceType: "version",
		label: "Platform version deprecated"
	},
	"platform_channel.create": {
		resourceType: "platform_channel",
		label: "Platform channel created"
	},
	"platform_channel.update": {
		resourceType: "platform_channel",
		label: "Platform channel updated"
	},
	"platform_channel.delete": {
		resourceType: "platform_channel",
		label: "Platform channel deleted"
	},
	"platform_channel.pin": {
		resourceType: "platform_channel",
		label: "Platform channel pinned"
	},
	"platform_channel.advance": {
		resourceType: "platform_channel",
		label: "Platform channel advanced"
	},
	"platform.crate-names.replace": {
		resourceType: "crate_name",
		label: "Platform crate names replaced"
	},
	"deployment_config.set": {
		resourceType: "deployment_config",
		label: "Deployment setting changed"
	},
	"deployment_config.converge": {
		resourceType: "deployment_config",
		label: "Deployment settings converged"
	},
	"dp_config_override.create": {
		resourceType: "dp_config_override",
		label: "Data plane config override created"
	},
	"dp_config_override.delete": {
		resourceType: "dp_config_override",
		label: "Data plane config override deleted"
	},
	"release_stage.create": {
		resourceType: "release_stage",
		label: "Release stage created"
	},
	"data_plane.operator_cert_signed": {
		resourceType: "data_plane",
		label: "Operator certificate signed"
	},
	"data_plane.created": {
		resourceType: "data_plane",
		label: "Data plane created"
	},
	"data_plane.create_rejected_base_domain_mismatch": {
		resourceType: "data_plane",
		label: "Data plane create refused: base domain"
	},
	"data_plane.create_rejected_gcp_project": {
		resourceType: "data_plane",
		label: "Data plane create refused: Google Cloud project"
	},
	"data_plane.reprovision_rejected_base_domain_mismatch": {
		resourceType: "data_plane",
		label: "Data plane reprovision refused: base domain"
	},
	"data_plane.reprovision_rejected_gcp_project": {
		resourceType: "data_plane",
		label: "Data plane reprovision refused: Google Cloud project"
	},
	"data_plane.image_resolved": {
		resourceType: "data_plane",
		label: "Data plane image chosen"
	},
	"data_plane.config_updated": {
		resourceType: "data_plane",
		label: "Data plane config updated"
	},
	"data_plane.machine_types_updated": {
		resourceType: "data_plane",
		label: "Data plane machine types updated"
	},
	"data_plane.satellite_enabled": {
		resourceType: "data_plane",
		label: "Satellite enabled"
	},
	"data_plane.satellite_disabled": {
		resourceType: "data_plane",
		label: "Satellite disabled"
	},
	"data_plane.satellite_regions_updated": {
		resourceType: "data_plane",
		label: "Satellite region policy updated"
	},
	"data_plane.setup_token_regenerated": {
		resourceType: "data_plane",
		label: "Setup token issued again"
	},
	"data_plane.operator.reissue_bootstrap": {
		resourceType: "data_plane",
		label: "Operator bootstrap credential issued again"
	},
	"data_plane.secrets_rotated": {
		resourceType: "data_plane",
		label: "Data plane secrets rotated"
	},
	"data_plane.credentials_rotated": {
		resourceType: "data_plane",
		label: "Data plane credentials rotated"
	},
	"data_plane.disabled": {
		resourceType: "data_plane",
		label: "Data plane disabled"
	},
	"data_plane.restored": {
		resourceType: "data_plane",
		label: "Data plane restored"
	},
	"data_plane.drained": {
		resourceType: "data_plane",
		label: "Data plane drained"
	},
	"data_plane.cancel": {
		resourceType: "data_plane",
		label: "Data plane provisioning cancelled"
	},
	"data_plane.force_destroy": {
		resourceType: "data_plane",
		label: "Data plane force destroy started"
	},
	"data_plane.decommissioned": {
		resourceType: "data_plane",
		label: "Data plane decommissioned"
	},
	"data_plane.teardown_residue": {
		resourceType: "data_plane",
		label: "Data plane teardown left residue"
	},
	"data_plane.database_dropped": {
		resourceType: "data_plane",
		label: "Data plane database dropped"
	},
	"data_plane.database_protection_lifted": {
		resourceType: "data_plane",
		label: "Data plane database protection lifted"
	},
	"dp.complete": {
		resourceType: "data_plane",
		label: "Provisioning job completed"
	},
	"dp.fail": {
		resourceType: "data_plane",
		label: "Provisioning job failed"
	},
	"dp.abandon": {
		resourceType: "data_plane",
		label: "Provisioning job abandoned"
	},
	"dp.force_cleanup": {
		resourceType: "data_plane",
		label: "Provisioning job force cleaned"
	},
	"data_plane.org_migrated": {
		resourceType: "data_plane",
		label: "Organization moved to the data plane"
	},
	"data_plane.retry": {
		resourceType: "data_plane",
		label: "Data plane provisioning retried"
	},
	"data_plane.resume": {
		resourceType: "data_plane",
		label: "Data plane provisioning resumed"
	},
	"data_plane.setup_completed": {
		resourceType: "data_plane",
		label: "Data plane setup marked complete"
	},
	"data_plane.upgrade_executed": {
		resourceType: "data_plane",
		label: "Data plane upgrade started"
	},
	"data_plane.force_upgrade": {
		resourceType: "data_plane",
		label: "Data plane force upgrade started"
	},
	"data_plane.operator_restarted": {
		resourceType: "data_plane",
		label: "Data plane operator restarted"
	},
	"data_plane.device_mtls_reconciled": {
		resourceType: "data_plane",
		label: "Device mTLS reconciled"
	},
	"data_plane.reprovisioned": {
		resourceType: "data_plane",
		label: "Data plane rebuilt"
	},
	"app_provisioning.provision": {
		resourceType: "app_provisioning",
		label: "App provisioned on a data plane"
	},
	"app_provisioning.deprovision": {
		resourceType: "app_provisioning",
		label: "App removed from a data plane"
	},
	"data_plane.etcd_encryption_reconciled": {
		resourceType: "data_plane",
		label: "Cluster encryption corrected"
	},
	"data_plane.node_pools_reconciled": {
		resourceType: "data_plane",
		label: "Node pools corrected"
	},
	"data_plane.sizing_reconciled": {
		resourceType: "data_plane",
		label: "Data plane sizing corrected"
	},
	"data_plane.satellite_reconciled": {
		resourceType: "data_plane",
		label: "Satellite corrected"
	},
	"data_plane.app_pool_node_groups_reconciled": {
		resourceType: "data_plane",
		label: "App pool node groups corrected"
	},
	"data_plane.pool_reroll_advanced": {
		resourceType: "data_plane",
		label: "Node pool reroll advanced"
	},
	"data_plane.plan_backfilled": {
		resourceType: "data_plane",
		label: "Data plane plan sizing filled in"
	},
	"data_plane.machine_type_defaults_backfilled": {
		resourceType: "data_plane",
		label: "Default machine types filled in"
	},
	"data_plane.dns_records_written": {
		resourceType: "data_plane",
		label: "DNS records written"
	},
	"data_plane.secrets_healed": {
		resourceType: "data_plane",
		label: "Data plane secrets repaired"
	},
	"data_plane.ready": {
		resourceType: "data_plane",
		label: "Data plane ready"
	},
	"data_plane.failed": {
		resourceType: "data_plane",
		label: "Data plane provisioning failed"
	},
	"relocation.created": {
		resourceType: "data_plane",
		label: "Bucket relocation planned"
	},
	"relocation.approved": {
		resourceType: "data_plane",
		label: "Bucket relocation gate approved"
	},
	"relocation.aborted": {
		resourceType: "data_plane",
		label: "Bucket relocation aborted"
	},
	"relocation.completed": {
		resourceType: "data_plane",
		label: "Bucket relocation completed"
	},
	"onboarding.client_secret_set": {
		resourceType: "data_plane",
		label: "Onboarding client secret set"
	},
	dp_access_requested: {
		resourceType: "dp_access_request",
		label: "Data plane access requested"
	},
	dp_access_approved: {
		resourceType: "dp_access_request",
		label: "Data plane access approved"
	},
	dp_access_denied: {
		resourceType: "dp_access_request",
		label: "Data plane access denied"
	},
	dp_access_withdrawn: {
		resourceType: "dp_access_request",
		label: "Data plane access request withdrawn"
	},
	dp_access_revoked: {
		resourceType: "dp_access_request",
		label: "Data plane access revoked"
	},
	dp_access_request_expired: {
		resourceType: "dp_access_request",
		label: "Data plane access request expired"
	},
	dp_access_ended: {
		resourceType: "dp_access_request",
		label: "Data plane access ended"
	},
	"dp_telemetry.ticket_open": {
		resourceType: "data_plane",
		label: "Data plane telemetry ticket opened"
	},
	"dp_telemetry.ticket_close": {
		resourceType: "data_plane",
		label: "Data plane telemetry ticket closed"
	}
};
/** The longest action string the vocabulary admits. The site-admin-api proxy filter bounds at 120. */
const CP_AUDIT_ACTION_MAX_LENGTH = 120;
/** The grammar of an action string: lower case words joined by `_`, `.` or `-`. */
const CP_AUDIT_ACTION_PATTERN = /^[a-z][a-z0-9]*(?:[._-][a-z0-9]+)*$/;
/** Return the keys of a non-empty record as a non-empty tuple, the form `z.enum` takes. */
function keysOf(record) {
	const [first, ...rest] = Object.keys(record);
	return [first, ...rest];
}
/** Every action string, in declaration order. The tuple form feeds `z.enum`. */
const CP_AUDIT_ACTION_NAMES = keysOf(CP_AUDIT_ACTIONS);
/** Every object type string, in declaration order. The tuple form feeds `z.enum`. */
const CP_AUDIT_RESOURCE_TYPE_NAMES = keysOf(CP_AUDIT_RESOURCE_TYPES);
/**
* The kinds of actor that a row names (D-19 column `actor_kind`).
*
* - `operator`: a person acted through the console, the CLI or an MCP tool. The row carries the
*   operator id.
* - `service`: a CP service acted on its own account. cp-auth-api takes the service name from the
*   verified token and writes it to `actor_service`.
* - `system`: a reconciler, a timer or a boot hook acted. No person asked for the change.
* - `script`: `bin/cp-seed` or `bin/cp-operator` acted from a terminal (D-4).
* - `dp_operator`: a DP operator program reported the change over its mTLS channel.
*
* A row written before this plan has no kind. The read API returns `null` for it.
*/
const CP_AUDIT_ACTOR_KINDS = [
	"operator",
	"service",
	"system",
	"script",
	"dp_operator"
];
/**
* The outcomes a row may carry, the values of the `audit_log_outcome_check` constraint (FedRAMP
* AU-3). `success` means the action took effect, `failure` means a rule refused it, and `error`
* means a fault stopped it.
*/
const CP_AUDIT_OUTCOMES = [
	"success",
	"failure",
	"error"
];
/**
* Tell whether a string is an action of the vocabulary.
*
* Who calls it: the census spec, cp-auth-api before an in-process write, and the console when it
* reads an action from a URL. It reads own keys only, so `'toString'` and `'__proto__'` answer
* `false`. It never throws.
*/
function isCpAuditAction(value) {
	return Object.hasOwn(CP_AUDIT_ACTIONS, value);
}
/** Tell whether a string is an object type of the vocabulary. Own keys only; it never throws. */
function isCpAuditResourceType(value) {
	return Object.hasOwn(CP_AUDIT_RESOURCE_TYPES, value);
}
/**
* The console label of an action string.
*
* Input: any string, also an action that an old row carries and the vocabulary no longer holds.
* Output: the label for an action of the vocabulary; the input string unchanged for any
* other string, so the Audit page shows an old row as written. It never throws.
*/
function cpAuditActionLabel(action) {
	return isCpAuditAction(action) ? CP_AUDIT_ACTIONS[action].label : action;
}
/**
* The console label of an object type string.
*
* Input: any string. Output: the label for a type of the vocabulary; the input string unchanged
* for any other string. It never throws.
*/
function cpAuditResourceTypeLabel(resourceType) {
	return isCpAuditResourceType(resourceType) ? CP_AUDIT_RESOURCE_TYPES[resourceType].label : resourceType;
}
//#endregion
export { CP_AUDIT_ACTIONS, CP_AUDIT_ACTION_MAX_LENGTH, CP_AUDIT_ACTION_NAMES, CP_AUDIT_ACTION_PATTERN, CP_AUDIT_ACTOR_KINDS, CP_AUDIT_OUTCOMES, CP_AUDIT_RESOURCE_TYPES, CP_AUDIT_RESOURCE_TYPE_NAMES, cpAuditActionLabel, cpAuditResourceTypeLabel, isCpAuditAction, isCpAuditResourceType };

//# sourceMappingURL=vocabulary.js.map